import { useState, useEffect } from "react";
import { getRestaurants } from "../api/api";
import { Restaurant, FilterOptions } from "../types";
import RestaurantCard from "../components/restaurants/RestaurantCard";
import RestaurantFilters from "../components/restaurants/RestaurantFilters";
import { Skeleton } from "../components/ui/skeleton";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../components/ui/pagination";
import { Helmet } from "react-helmet";

const initialFilters: FilterOptions = {
  search: "",
  cuisine: "all",
  price_range: { min: 0, max: 50 },
  sort_by: "none",
  sort_order: "asc",
  min_rating: 0,
  max_distance: 30,
};

const ExploreRestaurants = () => {
  const [filters, setFilters] = useState<FilterOptions>(initialFilters);
  const [currentPage, setCurrentPage] = useState(1);
  const [restaurants, setRestaurants] = useState<Restaurant[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const perPage = 6;

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await getRestaurants(filters);
        setRestaurants(data);
      } catch (err) {
        setError("Failed to load restaurants");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [filters]);

  const handleFilterChange = (newFilters: FilterOptions) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setFilters(initialFilters);
    setCurrentPage(1);
  };

  const totalRestaurants = restaurants?.length || 0;
  const totalPages = Math.ceil(totalRestaurants / perPage);
  const startIndex = (currentPage - 1) * perPage;
  const currentRestaurants = restaurants?.slice(startIndex, startIndex + perPage) || [];

  const renderSkeletons = () => (
      Array(6)
          .fill(0)
          .map((_, index) => (
              <div key={index} className="overflow-hidden rounded-xl bg-white shadow-md dark:bg-gray-800">
                <Skeleton className="h-48 w-full" />
                <div className="p-6 space-y-4">
                  <div className="flex justify-between">
                    <Skeleton className="h-6 w-1/3" />
                    <Skeleton className="h-6 w-16" />
                  </div>
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-2/3" />
                  <div className="flex justify-between">
                    <Skeleton className="h-6 w-16" />
                    <Skeleton className="h-10 w-32" />
                  </div>
                </div>
              </div>
          ))
  );

  return (
      <>
        <Helmet>
          <title>Explore Restaurants | SurpriseTable</title>
          <meta
              name="description"
              content="Discover participating restaurants offering exclusive surprise meals for just €15."
          />
        </Helmet>

        <section className="py-16 transition-colors duration-200 bg-background dark:bg-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <RestaurantFilters
                on_filter_change={handleFilterChange}
                on_reset_filters={handleResetFilters}
                total_results={totalRestaurants}
                filters={filters}
            />

            {isLoading ? (
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {renderSkeletons()}
                </div>
            ) : error ? (
                <div className="my-12 rounded-lg bg-red-50 p-6 text-center dark:bg-red-900/20">
                  <h3 className="mb-2 text-lg font-semibold text-red-800 dark:text-red-200">
                    Error loading restaurants
                  </h3>
                  <p className="text-red-600 dark:text-red-300">{error}</p>
                </div>
            ) : currentRestaurants.length === 0 ? (
                <div className="my-12 rounded-lg bg-gray-50 p-6 text-center dark:bg-gray-800">
                  <h3 className="mb-2 text-lg font-semibold text-gray-800 dark:text-gray-200">
                    No restaurants found
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    Try adjusting your filters or search terms
                  </p>
                </div>
            ) : (
                <>
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {currentRestaurants.map((restaurant) => (
                        <RestaurantCard key={restaurant.id} restaurant={restaurant} />
                    ))}
                  </div>

                  {totalPages > 1 && (
                      <div className="mt-12 flex justify-center">
                        <Pagination>
                          <PaginationContent>
                            {currentPage > 1 && (
                                <PaginationItem>
                                  <PaginationPrevious onClick={() => setCurrentPage((page) => Math.max(1, page - 1))} />
                                </PaginationItem>
                            )}

                            {Array.from({ length: Math.min(totalPages, 5) }).map((_, index) => {
                              let pageNumber =
                                  totalPages <= 5
                                      ? index + 1
                                      : currentPage <= 3
                                          ? index + 1
                                          : currentPage >= totalPages - 2
                                              ? totalPages - 4 + index
                                              : currentPage - 2 + index;

                              if (pageNumber <= totalPages) {
                                return (
                                    <PaginationItem key={pageNumber}>
                                      <PaginationLink
                                          isActive={pageNumber === currentPage}
                                          onClick={() => setCurrentPage(pageNumber)}
                                      >
                                        {pageNumber}
                                      </PaginationLink>
                                    </PaginationItem>
                                );
                              }
                              return null;
                            })}

                            {currentPage < totalPages && (
                                <PaginationItem>
                                  <PaginationNext onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))} />
                                </PaginationItem>
                            )}
                          </PaginationContent>
                        </Pagination>
                      </div>
                  )}
                </>
            )}
          </div>
        </section>
      </>
  );
};

export default ExploreRestaurants;
